
<template>
  <div class="p-6">
    <!-- Header Section -->
    <div class="mb-6">
      <div class="text-[#111111] flex items-center justify-between">
        <h1 class="text-[24px] font-bold">{{ title }}</h1>
        <Button
            as-child
            v-if="hasAnyRole(['admin', 'runner'])"
            variant="default"
            class="gap-1"
        >
        <router-link to="/partners/create">
          <PlusIcon :size="16" />
          Добавить партнера
        </router-link>
        </Button>
      </div>
      <p class="text-[14px] text-[#111111] mt-1">Управление партнерами и их данными</p>
    </div>

    <!-- Data Table -->
    <div class="space-y-4">
      <div class="flex items-center">
        <Input
          v-model="filters.name"
          placeholder="Поиск по названию..."
          class="max-w-sm bg-white"
        />
      </div>

      <div class="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Название</TableHead>
              <TableHead>Город</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead>Продуктов</TableHead>
              <TableHead>Смена</TableHead>
              <TableHead class="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="partner in filteredPartners" :key="partner.id">
              <TableCell>{{ partner.id }}</TableCell>
              <TableCell class="font-medium">{{ partner.name }}</TableCell>
              <TableCell>{{ partner.city.name }}</TableCell>
              <TableCell>
                <Badge :variant="getStatusVariant(partner.status)">{{ getStatusText(partner.status) }}</Badge>
              </TableCell>
              <TableCell>{{ partner.product_count }}</TableCell>
              <TableCell>
                <Badge :variant="partner.on_shift ? 'success' : 'destructive'">{{ partner.on_shift ? 'На смене' : 'Неактивен' }}</Badge>
              </TableCell>
              <TableCell class="text-right">
                <div class="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" @click="editPartner(partner)">
                    <PencilIcon :size="16" />
                  </Button>
                  <Button variant="ghost" size="icon" @click="deletePartner(partner)">
                    <TrashIcon :size="16" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { PlusIcon, PencilIcon, TrashIcon } from 'lucide-vue-next'
import { getPartnersList } from '@/api/core'
import { useModalStore } from '@/store/modal';
import { useAuthAccess } from '@/composables/useAuthAccess'
const { hasPermission, hasAnyPermission, hasRole, hasAnyRole } = useAuthAccess()

const modal = useModalStore();
const title = 'Партнеры'
const { toast } = useToast()

interface Partner {
  id: number
  name: string
  phone: string
  telegram_id: number
  latitude: number
  longitude: number
  address: string
  city_id: number
  city: {
    id: number
    name: string
    code: string
  }
  product_count: number
  description: string
  legal_name: string
  legal_number: string
  bank_details: string | null
  is_premium: boolean
  type: string
  status: string
  on_shift: boolean
  registered_at: string
}

// Partners data
const partners = ref<Partner[]>([])

// Filters for table
const filters = ref({
  name: '',
})

// Filtered partners based on search
const filteredPartners = computed(() => {
  return partners.value.filter(partner => {
    if (filters.value.name && !partner.name.toLowerCase().includes(filters.value.name.toLowerCase())) {
      return false
    }
    return true
  })
})

// Format date for display
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// Get badge variant based on status
const getStatusVariant = (status: string) => {
  switch (status) {
    case 'new':
      return 'default'
    case 'approved':
      return 'destructive'
    case 'rejected':
      return 'secondary'
    case 'active':
      return 'success'
    default:
      return 'outline'
  }
}

const getStatusText = (status: string) => {
  switch (status) {
    case 'new':
      return 'Новый'
    case 'approved':
      return 'Подтвержден'
    case 'rejected':
      return 'Отклонен'
    case 'active':
      return 'Активен'
    default:
      return 'Неизвестно'
  }
}

// CRUD Operations
const addNewPartner = async() => {
  try {
        await modal.openModal('PartnerCreateModal', {})
        // props.refresh()
    } catch (err) {
        console.log('Отмена:', err)
    }
}

const editPartner = (partner: Partner) => {
  toast({
    title: 'Информация',
    description: `Редактирование партнера ${partner.name}`,
    variant: 'default',
  })
}

const deletePartner = (partner: Partner) => {
  toast({
    title: 'Информация',
    description: `Удаление партнера ${partner.name}`,
    variant: 'destructive',
  })
}
onMounted(async () => {
  // const response = await getPartnersList({})
  const response = {
    status: 200,
    data: {
      items: [
        {
          id: 1,
          name: 'ТОО MGT',
          country: {
            id: 1,
            name: 'Казахстан',
            code: 'KZ',
            phone_mask: '+7 (###) ###-##-##'
          },
          product_count: 10,
          status: 'active',
          on_shift: true,
          city: {
            id: 1,
            name: 'Алматы',
            country_id: 1,
            code: 'ALA',
            location: {
              latitude: 43.238949,
              longitude: 76.889709,
              zoom_level: 12
            }
          },
          address: 'ул. Центральная, 1',
          launchDate: '2025-05-15',
          contacts: [
            {
              id: '1',
              name: 'Иван Иванов',
              phone: '+7 (701) 123-45-67',
              position: 'manager'
            }
          ],
          menuPackage: 'base',
          mutualSettlements: 'weekly',
          coordinates: {
            latitude: 43.238949,
            longitude: 76.889709
          },
          schedule: [],
          additionalInfo: 'Примечание'
        },
        {
          id: 2,
          name: 'ИП Курага',
          country: {
            id: 1,
            name: 'Казахстан',
            code: 'KZ',
            phone_mask: '+7 (###) ###-##-##'
          },
          product_count: 25,
          status: 'new',
          city: {
            id: 2,
            name: 'Алматы',
            country_id: 1,
            code: 'AST',
            location: {
              latitude: 51.160522,
              longitude: 71.470360,
              zoom_level: 12
            }
          },
          address: 'пр. Победы, 10',
          launchDate: '2025-06-01',
          contacts: [
            {
              id: '2',
              name: 'Петр Петров',
              phone: '+7 (702) 765-43-21',
              position: 'owner'
            }
          ],
          menuPackage: 'premium',
          mutualSettlements: 'monthly',
          coordinates: {
            latitude: 51.160522,
            longitude: 71.470360
          },
          schedule: [],
          additionalInfo: 'Вход со двора'
        }
      ]
    }
  }
  if (response.status === 200 && response.data) {
    partners.value = response.data.items
  } else {
    toast({
      title: 'Не удалось загрузить список партнеров',
      description: response.data.message || 'Ошибка',
      variant: 'destructive',
    })
  }
})
</script>
